import type { Currency, Lang } from './api';

/**
 * Тексты витрины.
 *
 * Английский — основной: сайт открывается на нём. Русский и армянский —
 * по переключателю в шапке. Новый язык добавляется ещё одним блоком ниже,
 * без правок в страницах.
 *
 * Здесь только интерфейс. Названия чаёв и описания живут в базе, в таблицах
 * переводов, и приходят с сервера уже на нужном языке.
 *
 * ⚠ Армянские тексты — черновой перевод, их надо вычитать. Весь вординг
 * главной сверяется отдельной задачей в Notion.
 */

type Money = (amount: string) => string;

interface Strings {
  tagline: string;
  heroTitle: string;
  heroLede: string;
  all: string;
  catalog: string;
  about: string;
  cart: string;
  checkout: string;
  addToCart: string;
  inCart: string;
  outOfStock: string;
  hideOutOfStock: string;
  allOutOfStock: string;
  showOutOfStock: string;
  soldOut: string;
  onlyLeft: (n: number) => string;
  from: string;
  empty: string;
  emptyCart: string;
  goShopping: string;
  remove: string;
  subtotal: string;
  discount: string;
  delivery: string;
  total: string;
  free: string;
  quotedSeparately: string;
  /** Приписка к итогу, когда доставку посчитаем отдельно */
  plusDelivery: string;
  minOrderNotMet: Money;
  minOrder: Money;
  /** Минимум в драмах — для Армении, доставка сверху */
  minOrderArmenia: Money;
  /** Минимум в долларах — для всего мира, доставка сверху */
  minOrderWorld: Money;
  minOrderLeft: Money;
  contacts: string;
  name: string;
  phone: string;
  telegram: string;
  telegramHint: string;
  errName: string;
  errPhone: string;
  errAddress: string;
  checkFields: string;
  deliveryMethod: string;
  pickup: string;
  yerevan: string;
  armenia: string;
  international: string;
  address: string;
  addressHint: string;
  comment: string;
  payment: string;
  transfer: string;
  transferHint: string;
  cash: string;
  crypto: string;
  cryptoHint: string;
  placeOrder: string;
  sending: string;
  thanks: string;
  orderPlaced: string;
  orderNumber: string;
  whatNext: string;
  whatNextText: string;
  backHome: string;
  year: string;
  origin: string;
  region: string;
  brewing: string;
  notes: string;
  noDescription: string;
  loading: string;
  error: string;
  retry: string;
  photo: string;
  location: string;
}

export const STRINGS: Record<Lang, Strings> = {
  en: {
    tagline: 'go drink tea',
    heroTitle: 'Tea I love, selected for you',
    heroLede: 'My personal collection of high-quality tea, open to everyone.',
    all: 'All',
    catalog: 'Catalogue',
    about: 'About',
    cart: 'Cart',
    checkout: 'Checkout',
    addToCart: 'Add to cart',
    inCart: 'In cart',
    outOfStock: 'Out of stock',
    hideOutOfStock: 'Hide out of stock',
    allOutOfStock: 'Everything here is out of stock right now',
    showOutOfStock: 'Show them anyway',
    soldOut: 'Sold out',
    onlyLeft: (n) => `only ${n} left`,
    from: 'from',
    empty: 'Nothing here yet',
    emptyCart: 'Your cart is empty',
    goShopping: 'Browse the catalogue',
    remove: 'Remove',
    subtotal: 'Items',
    discount: 'Discount',
    delivery: 'Delivery',
    total: 'Total',
    free: 'free',
    quotedSeparately: 'quoted separately',
    plusDelivery: '+ delivery',
    minOrderNotMet: (a) => `Minimum order is ${a}, delivery not included`,
    minOrder: (a) => `Minimum order ${a}`,
    minOrderArmenia: (a) => `Minimum order in Armenia ${a}\u00a0+\u00a0delivery`,
    minOrderWorld: (a) => `Minimum order worldwide ${a}\u00a0+\u00a0shipping`,
    minOrderLeft: (a) => `${a} more to reach the minimum order`,
    contacts: 'Contact',
    name: 'Name',
    phone: 'Phone',
    telegram: 'Telegram username',
    telegramHint: 'I will message you there after the order — the fastest way',
    errName: 'How should I call you?',
    errPhone: 'Phone number needed — at least 8 digits',
    errAddress: 'Where to deliver? Street and building',
    checkFields: 'Please check the fields above',
    deliveryMethod: 'Delivery',
    pickup: 'Pickup',
    yerevan: 'Yerevan courier',
    armenia: 'Within Armenia',
    international: 'International',
    address: 'Address',
    addressHint: 'Street, building, flat',
    comment: 'Order note',
    payment: 'Payment',
    transfer: 'Bank transfer',
    transferHint: 'I will send the details once the order is confirmed',
    cash: 'Cash on delivery',
    crypto: 'Crypto wallet transfer',
    cryptoHint: 'I will send the wallet address once the order is confirmed',
    placeOrder: 'Place order',
    sending: 'Sending…',
    thanks: 'thank you',
    orderPlaced: 'Order received',
    orderNumber: 'Order number',
    whatNext: 'What happens next',
    whatNextText:
      'I will message you on Telegram or call shortly to confirm the order, quote delivery and send the transfer details.',
    backHome: 'Back home',
    year: 'Year',
    origin: 'Origin',
    region: 'Region',
    brewing: 'How to brew',
    notes: 'In the cup',
    noDescription: 'Description coming soon.',
    loading: 'Loading…',
    error: 'Something went wrong',
    retry: 'Try again',
    photo: 'photo soon',
    location: 'Yerevan, Armenia',
  },

  ru: {
    tagline: 'давай пить чай',
    heroTitle: 'Я выбираю чай, чтобы он тебя порадовал',
    heroLede: 'Моя персональная коллекция высококачественного чая, открытая для всех.',
    all: 'Все',
    catalog: 'Каталог',
    about: 'О проекте',
    cart: 'Корзина',
    checkout: 'Оформить заказ',
    addToCart: 'В корзину',
    inCart: 'В корзине',
    outOfStock: 'Нет в наличии',
    hideOutOfStock: 'Скрыть то, чего нет в наличии',
    allOutOfStock: 'Сейчас здесь всё закончилось',
    showOutOfStock: 'Всё равно показать',
    soldOut: 'Закончился',
    onlyLeft: (n) => `осталось только ${n}`,
    from: 'от',
    empty: 'Здесь пока пусто',
    emptyCart: 'В корзине пока пусто',
    goShopping: 'Перейти в каталог',
    remove: 'Убрать',
    subtotal: 'Товары',
    discount: 'Скидка',
    delivery: 'Доставка',
    total: 'Итого',
    free: 'бесплатно',
    quotedSeparately: 'считаем отдельно',
    plusDelivery: '+ доставка',
    minOrderNotMet: (a) => `Минимальная сумма заказа — ${a}, без доставки`,
    minOrder: (a) => `Минимальный заказ — ${a}`,
    minOrderArmenia: (a) => `Минимальный заказ по Армении — ${a}\u00a0+\u00a0доставка`,
    minOrderWorld: (a) => `Минимальный заказ по всему миру — ${a}\u00a0+\u00a0доставка`,
    minOrderLeft: (a) => `До минимального заказа не хватает ${a}`,
    contacts: 'Контакты',
    name: 'Имя',
    phone: 'Телефон',
    telegram: 'Ник в Telegram',
    telegramHint: 'Напишу вам туда после заказа — так быстрее всего',
    errName: 'Как к вам обращаться?',
    errPhone: 'Нужен телефон — хотя бы 8 цифр',
    errAddress: 'Куда везти? Улица и дом',
    checkFields: 'Проверьте поля, отмеченные выше',
    deliveryMethod: 'Доставка',
    pickup: 'Самовывоз',
    yerevan: 'Курьер по Еревану',
    armenia: 'По Армении',
    international: 'За границу',
    address: 'Адрес',
    addressHint: 'Улица, дом, квартира',
    comment: 'Комментарий к заказу',
    payment: 'Оплата',
    transfer: 'Перевод на карту',
    transferHint: 'Реквизиты пришлю после подтверждения заказа',
    cash: 'Наличные при получении',
    crypto: 'Перевод на криптокошелёк',
    cryptoHint: 'Адрес кошелька пришлю после подтверждения заказа',
    placeOrder: 'Отправить заказ',
    sending: 'Отправляем…',
    thanks: 'спасибо',
    orderPlaced: 'Заказ принят',
    orderNumber: 'Номер заказа',
    whatNext: 'Что дальше',
    whatNextText:
      'Я напишу вам в Telegram или позвоню в ближайшее время — подтвержу состав, посчитаю доставку и пришлю реквизиты для перевода.',
    backHome: 'На главную',
    year: 'Год',
    origin: 'Происхождение',
    region: 'Регион',
    brewing: 'Как заваривать',
    notes: 'В чашке',
    noDescription: 'Описание скоро появится.',
    loading: 'Загружаем…',
    error: 'Что-то пошло не так',
    retry: 'Попробовать снова',
    photo: 'фото скоро',
    location: 'Ереван, Армения',
  },

  hy: {
    tagline: 'արի թեյ խմենք',
    heroTitle: 'Ընտրում եմ թեյ, որ այն ուրախացնի քեզ',
    heroLede: 'Բարձրորակ թեյի իմ անձնական հավաքածուն՝ բաց բոլորի համար։',
    all: 'Բոլորը',
    catalog: 'Կատալոգ',
    about: 'Նախագծի մասին',
    cart: 'Զամբյուղ',
    checkout: 'Ձևակերպել պատվերը',
    addToCart: 'Ավելացնել զամբյուղ',
    inCart: 'Զամբյուղում է',
    outOfStock: 'Առկա չէ',
    hideOutOfStock: 'Թաքցնել առկա չեղածը',
    allOutOfStock: 'Այստեղ հիմա ամեն ինչ սպառված է',
    showOutOfStock: 'Միևնույն է ցույց տալ',
    soldOut: 'Սպառված է',
    onlyLeft: (n) => `մնացել է ընդամենը ${n}`,
    from: 'սկսած',
    empty: 'Այստեղ դեռ դատարկ է',
    emptyCart: 'Զամբյուղը դատարկ է',
    goShopping: 'Անցնել կատալոգ',
    remove: 'Հեռացնել',
    subtotal: 'Ապրանքներ',
    discount: 'Զեղչ',
    delivery: 'Առաքում',
    total: 'Ընդամենը',
    free: 'անվճար',
    quotedSeparately: 'կհաշվարկենք առանձին',
    plusDelivery: '+ առաքում',
    minOrderNotMet: (a) => `Պատվերի նվազագույն գումարը՝ ${a}, առանց առաքման`,
    minOrder: (a) => `Նվազագույն պատվեր՝ ${a}`,
    minOrderArmenia: (a) => `Նվազագույն պատվեր Հայաստանում՝ ${a}\u00a0+\u00a0առաքում`,
    minOrderWorld: (a) => `Նվազագույն պատվեր ամբողջ աշխարհում՝ ${a}\u00a0+\u00a0առաքում`,
    minOrderLeft: (a) => `Նվազագույն պատվերին պակասում է ${a}`,
    contacts: 'Կոնտակտներ',
    name: 'Անուն',
    phone: 'Հեռախոս',
    telegram: 'Telegram օգտանուն',
    telegramHint: 'Պատվերից հետո կգրեմ Ձեզ այնտեղ․ այդպես ամենաարագն է',
    errName: 'Ինչպե՞ս դիմեմ Ձեզ',
    errPhone: 'Անհրաժեշտ է հեռախոսահամար՝ առնվազն 8 թիվ',
    errAddress: 'Ո՞ւր առաքել։ Փողոց և շենք',
    checkFields: 'Ստուգեք վերևում նշված դաշտերը',
    deliveryMethod: 'Առաքում',
    pickup: 'Տեղից վերցնել',
    yerevan: 'Առաքիչ Երևանում',
    armenia: 'Հայաստանի տարածքում',
    international: 'Արտասահման',
    address: 'Հասցե',
    addressHint: 'Փողոց, շենք, բնակարան',
    comment: 'Մեկնաբանություն պատվերին',
    payment: 'Վճարում',
    transfer: 'Փոխանցում քարտին',
    transferHint: 'Տվյալները կուղարկեմ պատվերը հաստատելուց հետո',
    cash: 'Կանխիկ՝ ստանալիս',
    crypto: 'Փոխանցում կրիպտոդրամապանակին',
    cryptoHint: 'Դրամապանակի հասցեն կուղարկեմ պատվերը հաստատելուց հետո',
    placeOrder: 'Ուղարկել պատվերը',
    sending: 'Ուղարկում ենք…',
    thanks: 'շնորհակալություն',
    orderPlaced: 'Պատվերն ընդունված է',
    orderNumber: 'Պատվերի համար',
    whatNext: 'Ինչ է լինելու հետո',
    whatNextText:
      'Շուտով կգրեմ Ձեզ Telegram-ով կամ կզանգեմ՝ կհաստատեմ պատվերը, կհաշվեմ առաքումը և կուղարկեմ փոխանցման տվյալները։',
    backHome: 'Գլխավոր էջ',
    year: 'Տարի',
    origin: 'Ծագում',
    region: 'Տարածաշրջան',
    brewing: 'Ինչպես թրմել',
    notes: 'Բաժակում',
    noDescription: 'Նկարագրությունը շուտով։',
    loading: 'Բեռնվում է…',
    error: 'Ինչ-որ բան սխալ գնաց',
    retry: 'Կրկին փորձել',
    photo: 'լուսանկարը շուտով',
    location: 'Երևան, Հայաստան',
  },
};

export type { Strings };

/** Подписи языков в переключателе — каждый на своём языке */
export const LANG_LABEL: Record<Lang, string> = {
  en: 'EN',
  hy: 'ՀԱՅ',
  ru: 'RU',
};

/** Порядок в переключателе: основной первым */
export const LANGS: Lang[] = ['en', 'hy', 'ru'];

/**
 * Формат денег.
 *
 * Драмы — целые, знак после суммы и с неразрывным пробелом, чтобы «5 000 ֏»
 * не разорвало на две строки. Доллары приходят в центах.
 */
export function money(amount: number, currency: Currency, lang: Lang): string {
  if (currency === 'USD') {
    const locale = lang === 'ru' ? 'ru-RU' : lang === 'hy' ? 'hy-AM' : 'en-US';
    return `$${(amount / 100).toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
  return `${amount.toLocaleString('ru-RU').replace(/\s/g, ' ')} ֏`;
}
